import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import CoreHttpHandler from 'http/services/CoreHttpHandler';
import WebSocket from 'app/socket/WebSocket';
import history from '@history';
import Swal from 'sweetalert2';

/**
 * Channel-agnostic notifications context.
 *
 * - Loads the client's enabled channels via `saChannels.clientChannelsList`.
 * - Loads notifications from one of two endpoints based on the active tab:
 *     - tab === 'all'    → `notifications.notificationsGetAll`
 *     - tab === 'unread' → `notifications.notificationsGetUnread`
 *   The selected channel id is sent as `channelId`; sending no `channelId`
 *   asks the backend for ALL channels.
 * - Exposes `markAsRead(id)` which hits `notifications.notificationsMarkAsRead`
 *   and updates local state optimistically.
 */

const NotificationsContext = createContext(null);

export const useNotifications = () => {
	const ctx = useContext(NotificationsContext);
	if (!ctx) {
		throw new Error(
			'useNotifications must be used inside a NotificationsProvider'
		);
	}
	return ctx;
};

// 'all' = no channel filter applied (don't send channelId to the API).
const ALL_CHANNELS = 'all';

// Read auth state at call time — the provider mounts above auth, so values
// captured at mount would be stale (or missing) until the user logs in.
const hasAuthToken = () => Boolean(localStorage.getItem('user_token'));
const getClientId = () => {
	try {
		const ud = JSON.parse(localStorage.getItem('user_data')) || {};
		return ud?.client_id || null;
	} catch (e) {
		return null;
	}
};

// Super-admin context (client_id === "0") has no client-scoped channels or
// notifications, so skip both endpoints entirely — they'd just 400/return
// empty. Read live (same reasoning as the auth helpers above).
const isSuperAdminClient = () => String(getClientId()) === '0';

// Agent online/offline gate. The inbox is hidden until the agent is online,
// so opening a notification target while offline would just dump them on a
// blocked screen.
const isAgentOnline = () => {
	try {
		return JSON.parse(localStorage.getItem('online')) === true;
	} catch (e) {
		return false;
	}
};

// Play the same beep the inbox uses when a new ticket arrives.
// Preloaded singleton so the audio element survives between calls (a fresh
// `new Audio()` per ping can get GC'd before `play()` resolves on some
// browsers, which made the beep silently fail).
const NOTIFICATION_SOUND_SRC = '/assets/sounds/chat-notification.mp3';
let beepAudio = null;
const getBeepAudio = () => {
	if (!beepAudio && typeof Audio !== 'undefined') {
		beepAudio = new Audio(NOTIFICATION_SOUND_SRC);
		beepAudio.preload = 'auto';
		beepAudio.volume = 1.0;
		// Trigger a load so the file is cached and ready when the first
		// socket arrives.
		try { beepAudio.load(); } catch (e) { /* ignore */ }
	}
	return beepAudio;
};
const playNotificationBeep = () => {
	try {
		const audio = getBeepAudio();
		if (!audio) return;
		// Rewind so consecutive arrivals replay from the start instead of
		// stalling because the element is still finishing the previous play.
		try { audio.currentTime = 0; } catch (e) { /* ignore — some browsers */ }
		const p = audio.play();
		if (p && typeof p.catch === 'function') {
			p.catch(err => {
				// Browser autoplay policy blocks audio until the page has
				// received a user gesture. Log so it's diagnosable instead of
				// looking like the feature is broken.
				// eslint-disable-next-line no-console
				console.warn('[notifications] beep blocked:', err?.message || err);
			});
		}
	} catch (e) {
		// eslint-disable-next-line no-console
		console.warn('[notifications] beep failed:', e);
	}
};

// Deterministic key calculable from BOTH socket payloads and API rows. Used
// to dedup a socket-pushed notification once the same event shows up in the
// API list (the socket has no `notification_id` today, so we can't dedup by
// id alone — when the backend starts sending one we'll prefer it).
const syntheticKey = n => {
	if (!n) return '';
	const reactionType =
		n.payload?.reaction_type || n.reaction_type || '';
	return [
		(n.channel_type || '').toLowerCase(),
		(n.event_type || '').toLowerCase(),
		String(n.reference_id || ''),
		String(n.actor_id || n.customer_fb_id || ''),
		String(reactionType)
	].join(':');
};

// Convert a raw Facebook socket payload into the notification shape the
// sidebar already renders. Returns null when the event shouldn't surface
// (e.g. an `edit`/`remove` reaction change is noise, not a new event).
let socketSeq = 0;
const buildSocketNotification = (eventName, data) => {
	if (!data) return null;
	// Only 'add' type events are user-visible new notifications. 'edit' on a
	// reaction means the customer changed their reaction type — surfacing
	// that as a fresh notification would be noisy.
	if (data.type && data.type !== 'add') return null;
	const customer = data.customer || {};
	const actor_name = customer.name || 'Someone';
	const actor_id = customer.id || data.customer_fb_id || null;
	const actor_profile_pic = customer.profile_pic || null;
	const base = {
		channel_id: data.facebook_page_id || null,
		channel_type: 'facebook',
		actor_id,
		actor_name,
		actor_profile_pic,
		customer_fb_id: data.customer_fb_id || null,
		is_read: false,
		dt: data.dt || new Date().toISOString(),
		// Backend will start sending `notification_id` later — honour it.
		__sourceSocket: true
	};
	// Synthetic id keeps React happy until we have a real notification_id.
	const fallbackId = `socket-${eventName}-${data.id || ++socketSeq}`;
	if (eventName === 'fbPostReaction') {
		const reactionType = data.reaction_type || 'like';
		return {
			...base,
			id: data.notification_id || fallbackId,
			event_type: 'post_reaction',
			reference_id: String(data.fb_post_id || data.post?.id || ''),
			reference_type: 'post',
			title: `${actor_name} reacted to your post`,
			description: `${actor_name} added a ${reactionType} reaction`,
			payload: {
				page_id: data.page?.id || null,
				post_id: data.post?.id || null,
				reaction_type: reactionType,
				customer_fb_id: data.customer_fb_id || null
			}
		};
	}
	if (eventName === 'fbPostComment') {
		const isReply = Boolean(data.parent_comment_id);
		return {
			...base,
			id: data.notification_id || fallbackId,
			event_type: isReply ? 'comment_reply' : 'post_comment',
			reference_id: String(data.id || ''),
			reference_type: 'comment',
			title: isReply
				? `${actor_name} replied to a comment`
				: `${actor_name} commented on your post`,
			description: data.message || '',
			payload: {
				page_id: data.page?.id || null,
				post_id: data.post?.id || null,
				comment_id: data.id || null,
				parent_comment_id: data.parent_comment_id || null
			}
		};
	}
	if (eventName === 'fbCommentReaction') {
		const reactionType = data.reaction_type || 'like';
		return {
			...base,
			id: data.notification_id || fallbackId,
			event_type: 'comment_reaction',
			reference_id: String(data.fb_comment_id || data.comment?.id || ''),
			reference_type: 'comment',
			title: `${actor_name} reacted to a comment`,
			description: `${actor_name} added a ${reactionType} reaction`,
			payload: {
				page_id: data.page?.id || null,
				post_id: data.fb_post_id || null,
				comment_id: data.comment?.id || data.fb_comment_id || null,
				reaction_type: reactionType
			}
		};
	}
	return null;
};

export const NotificationsProvider = ({ children }) => {

	const [notifications, setNotifications] = useState([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [open, setOpen] = useState(false);
	const [activeTab, setActiveTab] = useState('all'); // 'all' | 'unread'
	const [activeChannelId, setActiveChannelId] = useState(ALL_CHANNELS);
	const [channels, setChannels] = useState([]);
	const [channelsLoading, setChannelsLoading] = useState(false);
	// Pagination state — `limit` is sized by the sidebar to fit the visible
	// area so the first fetch covers exactly one screen; subsequent pages
	// load on scroll.
	const [page, setPage] = useState(0);
	const [limit, setLimit] = useState(10);
	const [totalItems, setTotalItems] = useState(0);
	// Monotonic counter the sidebar bumps when a notification is clicked while
	// the user is already on /apps/chat. ChatApp watches this and switches
	// into FB Posts mode without us pushing a fresh location (no Suspense
	// fallback flash, no apparent "reload").
	const [enterFbPostsRequest, setEnterFbPostsRequest] = useState(0);
	// In-flight Facebook-style toasts triggered by socket arrivals. Each toast
	// is just a notification with a synthetic toastId so we can dismiss it
	// independently of whether the user opens the drawer.
	const [socketToasts, setSocketToasts] = useState([]);
	// Cross-context intent for Facebook notifications: when the user clicks
	// a FB notification we drop the post/page/comment ids in here so
	// FacebookPostsView can pick them up and open the single-post dialog
	// once it's mounted.
	const [pendingFbView, setPendingFbView] = useState(null);

	// Track the latest request so a stale response (slow network + quick
	// tab/channel flip) can't overwrite the current view.
	const requestSeq = useRef(0);

	const openSidebar = useCallback(() => setOpen(true), []);
	const closeSidebar = useCallback(() => setOpen(false), []);
	const toggleSidebar = useCallback(() => setOpen(o => !o), []);

	// ---------- Channels (for the filter dropdown) ----------
	const loadChannels = useCallback(() => {
		// Skip silently when the user isn't authenticated yet — the provider
		// mounts above auth so the first effect can fire pre-login.
		if (!hasAuthToken()) return;
		// Super-admin (client_id "0") has no client channels — don't call.
		if (isSuperAdminClient()) return;
		const clientId = getClientId();
		if (!clientId) return;
		setChannelsLoading(true);
		CoreHttpHandler.request(
			'saChannels',
			'clientChannelsList',
			{ clientId, isEnabled: true },
			response => {
				const list = response?.data?.data?.data || [];
				setChannels(list);
				setChannelsLoading(false);
			},
			() => {
				setChannels([]);
				setChannelsLoading(false);
			}
		);
	}, []);

	useEffect(() => {
		loadChannels();
	}, [loadChannels]);

	// Also refresh the channels list whenever the sidebar opens — guarantees
	// that if the first attempt happened before the user logged in, the list
	// is rebuilt the moment they actually go to use the drawer.
	useEffect(() => {
		if (open) loadChannels();
	}, [open, loadChannels]);

	// Track page in a ref too, so fetchNotifications can read the current
	// value without `page` ending up in its useCallback deps (which would
	// re-create the callback every time we paginate and re-trigger the
	// fresh-fetch effect into an infinite loop).
	const pageRef = useRef(0);
	useEffect(() => { pageRef.current = page; }, [page]);

	// ---------- Notifications ----------
	// `append` flag controls behaviour:
	//   false → fresh fetch, replaces the list, resets page to 0
	//   true  → load-more, appends to the list, advances the page
	const fetchNotifications = useCallback((opts = {}) => {
		const { append = false } = opts;
		// Same auth guard as channels — don't try to hit the API until the
		// user is logged in. The sidebar-open useEffect will retry.
		if (!hasAuthToken()) return;
		// Super-admin (client_id "0") has no client notifications — don't call.
		if (isSuperAdminClient()) return;
		const method =
			activeTab === 'unread' ? 'notificationsGetUnread' : 'notificationsGetAll';
		const requestedPage = append ? (pageRef.current || 0) + 1 : 0;
		const payload = {
			limit: limit || 10,
			page: requestedPage
		};
		if (activeChannelId && activeChannelId !== ALL_CHANNELS) {
			payload.channelId = String(activeChannelId);
		}
		const seq = ++requestSeq.current;
		if (append) setLoadingMore(true); else setLoading(true);
		CoreHttpHandler.request(
			'notifications',
			method,
			payload,
			response => {
				// Drop the response if a newer request has already gone out.
				if (seq !== requestSeq.current) return;
				const data = response?.data?.data || {};
				const list = Array.isArray(data.notifications) ? data.notifications : [];
				setTotalItems(typeof data.totalItems === 'number' ? data.totalItems : list.length);
				setPage(requestedPage);
				if (append) {
					// Dedup against current list by id when appending.
					setNotifications(prev => {
						const seen = new Set(prev.map(n => String(n.id)));
						const next = list.filter(n => !seen.has(String(n.id)));
						return [...prev, ...next];
					});
				} else {
					// Fresh fetch — merge with any socket-pushed entries that
					// haven't yet shown up in the API.
					const apiIds = new Set(list.map(n => String(n.id)));
					const apiKeys = new Set(list.map(syntheticKey));
					setNotifications(prev => {
						const orphans = prev.filter(
							n => n.__sourceSocket
								&& !apiIds.has(String(n.id))
								&& !apiKeys.has(syntheticKey(n))
						);
						return [...orphans, ...list];
					});
				}
				if (typeof data.unreadCount === 'number') {
					setUnreadCount(data.unreadCount);
				} else if (!append) {
					setUnreadCount(list.reduce((n, item) => n + (item?.is_read ? 0 : 1), 0));
				}
				if (append) setLoadingMore(false); else setLoading(false);
			},
			() => {
				if (seq !== requestSeq.current) return;
				if (!append) setNotifications([]);
				if (append) setLoadingMore(false); else setLoading(false);
			}
		);
	}, [activeTab, activeChannelId, limit]);

	// Convenience for the sidebar's infinite-scroll handler.
	const loadMoreNotifications = useCallback(() => {
		if (loading || loadingMore) return;
		if (notifications.length >= totalItems) return;
		fetchNotifications({ append: true });
	}, [loading, loadingMore, notifications.length, totalItems, fetchNotifications]);

	const hasMoreNotifications = notifications.length < totalItems;

	// Pull notifications whenever the tab/channel filter or page-size limit
	// changes, OR the sidebar opens (so the user always sees fresh data).
	// fetchNotifications resets to page 0 internally for fresh fetches.
	useEffect(() => {
		setPage(0);
		fetchNotifications();
	}, [fetchNotifications]);

	useEffect(() => {
		if (open) fetchNotifications();
	}, [open, fetchNotifications]);

	// ---------- Unlock audio on first user gesture ----------
	// Modern browsers block Audio.play() until the page has received a
	// gesture. We "prime" the singleton on the first click/touch/keydown so
	// later socket-driven beeps actually play.
	useEffect(() => {
		const unlock = () => {
			const audio = getBeepAudio();
			if (!audio) return;
			const wasMuted = audio.muted;
			audio.muted = true;
			const p = audio.play();
			const done = () => {
				try {
					audio.pause();
					audio.currentTime = 0;
				} catch (e) { /* ignore */ }
				audio.muted = wasMuted;
			};
			if (p && typeof p.then === 'function') {
				p.then(done).catch(done);
			} else {
				done();
			}
			document.removeEventListener('click', unlock, true);
			document.removeEventListener('keydown', unlock, true);
			document.removeEventListener('touchstart', unlock, true);
		};
		document.addEventListener('click', unlock, true);
		document.addEventListener('keydown', unlock, true);
		document.addEventListener('touchstart', unlock, true);
		return () => {
			document.removeEventListener('click', unlock, true);
			document.removeEventListener('keydown', unlock, true);
			document.removeEventListener('touchstart', unlock, true);
		};
	}, []);

	// ---------- Live socket events ----------
	// Subscribe once on mount. If the user isn't authenticated yet (so
	// WebSocket.getSocket() returns null), retry on a slow timer until the
	// socket is available; teardown both the timer and the listeners on
	// unmount.
	const activeChannelIdRef = useRef(activeChannelId);
	useEffect(() => { activeChannelIdRef.current = activeChannelId; }, [activeChannelId]);

	useEffect(() => {
		let socket = null;
		let retryTimer = null;
		let handlers = null;

		const pushSocketNotification = notif => {
			if (!notif) return;
			// Every socket-pushed notification today is a Facebook event, so it
			// is in view under the "All channels" filter and under a Facebook
			// channel filter alike. This used to be computed as
			// `activeChannelIdRef.current === ALL_CHANNELS || true`, which is
			// unconditionally true and read as if the filter were honoured.
			// It's spelled out here so the real behaviour is visible: when a
			// non-Facebook channel is selected the event is still listed.
			// Narrow this once the backend sends more than FB events.
			setNotifications(prev => {
				// Dedup against existing entries (id or synthetic key).
				const key = syntheticKey(notif);
				if (
					prev.some(
						n =>
							String(n.id) === String(notif.id) ||
							syntheticKey(n) === key
					)
				) {
					return prev;
				}
				return [notif, ...prev].slice(0, 100);
			});
			setUnreadCount(c => c + 1);
			// Drop a transient toast on screen for socket arrivals. The
			// `toastId` is what NotificationToast uses to dismiss it; the
			// underlying notification still keeps its `id` for mark-as-read.
			setSocketToasts(prev => {
				const toastId = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
				return [{ ...notif, toastId }, ...prev].slice(0, 5);
			});
			playNotificationBeep();
		};

		const attach = () => {
			socket = WebSocket.getSocket();
			if (!socket) {
				// Try again once we (hopefully) have an auth token.
				retryTimer = setTimeout(attach, 5000);
				return;
			}
			const onReact = data => pushSocketNotification(buildSocketNotification('fbPostReaction', data));
			const onComment = data => pushSocketNotification(buildSocketNotification('fbPostComment', data));
			const onCommentReact = data => pushSocketNotification(buildSocketNotification('fbCommentReaction', data));
			handlers = { onReact, onComment, onCommentReact };
			socket.on('fbPostReaction', onReact);
			socket.on('fbPostComment', onComment);
			socket.on('fbCommentReaction', onCommentReact);
		};
		attach();

		return () => {
			if (retryTimer) clearTimeout(retryTimer);
			if (socket && handlers) {
				socket.off('fbPostReaction', handlers.onReact);
				socket.off('fbPostComment', handlers.onComment);
				socket.off('fbCommentReaction', handlers.onCommentReact);
			}
		};
	}, []);

	// ---------- Mark all as read ----------
	// API requires { channelId, channelType }. When the user is viewing the
	// "All channels" filter we send neither field so the backend can handle
	// "everything" however it prefers (today: omit and let it 400 if it
	// requires the channel scope — wrap the call with our standard Swal
	// error so the user sees the API's message).
	const markAllAsRead = useCallback(() => {
		if (!hasAuthToken()) return;
		const payload = {};
		if (activeChannelId && activeChannelId !== ALL_CHANNELS) {
			payload.channelId = String(activeChannelId);
			const opt = channels.find(c => String(c.channel_id) === String(activeChannelId));
			if (opt?.channel_code) payload.channelType = opt.channel_code;
		}
		// Optimistic: flip everything currently visible to read.
		setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
		setUnreadCount(0);
		CoreHttpHandler.request(
			'notifications',
			'notificationsMarkAllAsRead',
			payload,
			() => {
				// Light refetch to pull the canonical state back from the server.
				fetchNotifications();
			},
			err => {
				// On failure, refetch to undo the optimistic flip.
				fetchNotifications();
				// Re-export of Swal error helper happens via the hook elsewhere
				// — here we just log so the dev console shows what went wrong;
				// the next fetch corrects the UI.
				// eslint-disable-next-line no-console
				console.warn('[notifications] markAllAsRead failed:', err?.response?.data?.message || err?.message);
			}
		);
	}, [activeChannelId, channels, fetchNotifications]);

	// ---------- Mark as read ----------
	const markAsRead = useCallback(notificationId => {
		if (!notificationId) return;
		// Optimistic: flip the local row to read so the UI updates instantly.
		setNotifications(prev =>
			prev.map(n =>
				String(n.id) === String(notificationId) ? { ...n, is_read: true } : n
			)
		);
		setUnreadCount(c => Math.max(0, c - 1));
		CoreHttpHandler.request(
			'notifications',
			'notificationsMarkAsRead',
			{ notificationId: String(notificationId) },
			() => {},
			() => {
				// On failure, refetch to resync with server state.
				fetchNotifications();
			}
		);
	}, [fetchNotifications]);

	// Convenience: dismiss removes the row from local state (no API for delete
	// yet, this is just a UI affordance).
	const dismissNotification = useCallback(id => {
		setNotifications(prev => prev.filter(n => String(n.id) !== String(id)));
	}, []);

	const clearAll = useCallback(() => setNotifications([]), []);

	// Bumped by the sidebar when a Facebook-channel notification is clicked
	// while the user is already on /apps/chat. ChatApp listens and flips into
	// FB Posts mode without us pushing a new location (which would otherwise
	// re-trigger the Suspense fallback and look like a reload).
	const requestEnterFbPosts = useCallback(
		() => setEnterFbPostsRequest(c => c + 1),
		[]
	);

	const dismissToast = useCallback(toastId => {
		setSocketToasts(prev => prev.filter(t => t.toastId !== toastId));
	}, []);

	// Single source of truth for "what happens when a notification is
	// activated". Used by both the sidebar tile and the live toast so they
	// behave identically — same offline gate, same mark-as-read, same
	// navigation, same FB Posts tab signal.
	const activateNotification = useCallback(n => {
		if (!n) return;
		if (!isAgentOnline()) {
			Swal.fire({
				title: "You're offline",
				text: 'Please go online from the header toggle to view this notification.',
				icon: 'warning',
				confirmButtonText: 'OK',
				width: '380px',
				heightAuto: false,
				scrollbarPadding: false
			});
			return;
		}
		if (!n.is_read) markAsRead(n.id);
		const channel = (n.channel_type || '').toLowerCase();
		const isFacebook = channel === 'facebook' || channel === 'facebook_post';
		if (isFacebook) {
			setEnterFbPostsRequest(c => c + 1);
			// Stash the post/comment intent so FacebookPostsView can
			// open the single-post dialog once it mounts.
			const payload = n.payload || {};
			const pageId = payload.page_id ?? n.channel_id ?? null;
			const postId = payload.post_id ?? n.reference_id ?? null;
			const commentId = payload.comment_id ?? null;
			if (pageId && postId) {
				setPendingFbView({
					pageId: String(pageId),
					postId: String(postId),
					commentId: commentId ? String(commentId) : null,
					eventType: n.event_type || null,
					notificationId: n.id != null ? String(n.id) : null
				});
			}
		}
		setOpen(false);
		const alreadyOnInbox = history.location.pathname === '/apps/inbox';
		if (!alreadyOnInbox) {
			history.push({ pathname: '/apps/inbox', state: { openFbPosts: isFacebook } });
		}
	}, [markAsRead]);

	const consumePendingFbView = useCallback(() => setPendingFbView(null), []);

	// Derived: list of unique channels actually represented in the channels
	// API payload. Pre-pended with an "All" option used as the default.
	const channelOptions = useMemo(() => {
		const opts = [{ id: ALL_CHANNELS, label: 'All channels', channelCode: null }];
		channels.forEach(c => {
			opts.push({
				id: String(c.channel_id),
				label: c.channel_name || c.channel_code || `Channel ${c.channel_id}`,
				channelCode: c.channel_code || null
			});
		});
		return opts;
	}, [channels]);

	const value = {
		// data
		notifications,
		unreadCount,
		loading,
		// sidebar
		open,
		openSidebar,
		closeSidebar,
		toggleSidebar,
		// tabs
		activeTab,
		setActiveTab,
		// channel filter
		activeChannelId,
		setActiveChannelId,
		channels,
		channelsLoading,
		channelOptions,
		ALL_CHANNELS,
		// pagination
		page,
		limit,
		setLimit,
		totalItems,
		loadingMore,
		hasMoreNotifications,
		loadMoreNotifications,
		// actions
		fetchNotifications,
		markAsRead,
		markAllAsRead,
		dismissNotification,
		clearAll,
		// cross-route signal for ChatApp to switch into FB Posts mode
		enterFbPostsRequest,
		requestEnterFbPosts,
		// shared click logic + transient socket toasts
		activateNotification,
		socketToasts,
		dismissToast,
		// FB-channel intent (post id, comment id) for FacebookPostsView to
		// consume and pop the single-post dialog
		pendingFbView,
		consumePendingFbView
	};

	return (
		<NotificationsContext.Provider value={value}>
			{children}
		</NotificationsContext.Provider>
	);
};

export default NotificationsContext;
